import { getSupabase } from './supabase'
import { reportHttpFailure } from '../dev/report-http'
import type { ApiError, ApiResponse } from '../types/api'

export const API_ERROR_EVENT = 'apiclient:error' as const

// gapi 组前缀机制(gapi-restful-regroup):4 个 SPA 各自挂在独立的 router group
// (/gapi/user|admin|agent|service),每个 SPA 入口启动时调一次 setGapiBase 声明自己
// 属于哪组;之后 shared 内部所有调用用 gapi('/auth/login') 形式拼出最终 URL。
// 注:钉死豁免 GET /gapi/file/{base_file_id}/{filename} 不走本机制(直接写原路径)。
export type GapiBase = '/gapi/user' | '/gapi/admin' | '/gapi/agent' | '/gapi/service'

let gapiBase: GapiBase = '/gapi/user'

export function setGapiBase(base: GapiBase): void {
  gapiBase = base
}

/** gapi('/auth/login') → '/gapi/<group>/auth/login'。path 必须以 '/' 开头。 */
export function gapi(path: string): string {
  return gapiBase + path
}

export type ApiClientErrorEventDetail = {
  code: string
  message: string
  status: number
  url: string
}

function dispatchApiError(detail: ApiClientErrorEventDetail): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail }))
}

export class ApiClientError extends Error {
  readonly code: string
  readonly status: number
  readonly details: unknown

  constructor(status: number, payload: ApiError) {
    super(payload.message)
    this.name = 'ApiClientError'
    this.code = payload.code
    this.status = status
    this.details = payload.details
  }
}

export type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  // silenceErrorCodes:本次请求中「故意容错」的错误码白名单。命中的 code 不派发
  // 全局 apiclient:error 事件(即不触发 GlobalErrorToast),其余 code(如 5xx /
  // 网络错)仍正常弹。用于进场探测类请求,如 pack 进场探订阅状态(401 TOKEN_INVALID
  // 走 preview 兜底)。无论是否命中,都仍 throw ApiClientError + reportHttpFailure
  // (调用方 .catch 与 dev 上报不受影响)。
  silenceErrorCodes?: readonly string[]
}

export type ApiClient = {
  get<T>(path: string, opts?: RequestOptions): Promise<T>
  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>
  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>
  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>
  del<T>(path: string, opts?: RequestOptions): Promise<T>
  request<T>(path: string, opts?: RequestOptions): Promise<T>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path
  const usp = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) usp.set(k, String(v))
  }
  const qs = usp.toString()
  return qs ? `${path}?${qs}` : path
}

async function authHeaders(): Promise<HeadersInit> {
  try {
    const sb = getSupabase()
    const { data } = await sb.auth.getSession()
    const token = data.session?.access_token
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, headers, silenceErrorCodes, ...rest } = opts
  // emitError:命中 silenceErrorCodes 的 code 不派发全局事件(GlobalErrorToast 监听),
  // 其余 code 照常派发。
  const emitError = (detail: ApiClientErrorEventDetail): void => {
    if (silenceErrorCodes?.includes(detail.code)) return
    dispatchApiError(detail)
  }
  const url = buildUrl(path, query)
  const auth = await authHeaders()
  const init: RequestInit = {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...auth,
      ...headers,
    },
    credentials: 'include',
  }
  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body)
  }
  const res = await fetch(url, init)
  const text = await res.text()

  // 2026-05-28:非 JSON 响应容错。
  // 历史 bug:L4 server 容器旧版本不含某 route → caddy 反代返回 plain text
  // "404 page not found",原 `JSON.parse` 抛 `SyntaxError`(Unexpected non-whitespace
  // character after JSON at position 4)冒泡到 UI,无 trace_id / status,极难定位。
  // 现在显式抛 `ApiClientError(status, NON_JSON_RESPONSE)`,UI 层可统一 catch。
  let json: unknown
  try {
    json = text ? JSON.parse(text) : undefined
  } catch {
    reportHttpFailure(url, res.status, text)
    const detail: ApiClientErrorEventDetail = {
      code: 'NON_JSON_RESPONSE',
      message: `server returned non-JSON (status=${res.status}): ${text.slice(0, 200)}`,
      status: res.status,
      url,
    }
    emitError(detail)
    throw new ApiClientError(res.status, { code: detail.code, message: detail.message })
  }

  // 错误响应：server 用 {code, message, trace_id, details} 形式（详见 api-rules.md §8.1）
  // 同时兼容 {success:false, error:{code,message,...}} envelope 形式
  if (!res.ok) {
    // L5 dev:把 4xx/5xx 上报给 error-reporter(prod CI gate 关 → no-op)
    reportHttpFailure(url, res.status, text)
    if (json && typeof json === 'object') {
      const obj = json as Record<string, unknown>
      if (obj.success === false && typeof obj.error === 'object' && obj.error !== null) {
        const err = obj.error as ApiError
        emitError({ code: err.code, message: err.message ?? '', status: res.status, url })
        throw new ApiClientError(res.status, err)
      }
      if (typeof obj.code === 'string') {
        const code = obj.code
        const message = typeof obj.message === 'string' ? obj.message : ''
        emitError({ code, message, status: res.status, url })
        throw new ApiClientError(res.status, { code, message, details: obj.details })
      }
    }
    const fallbackDetail: ApiClientErrorEventDetail = {
      code: 'NETWORK_ERROR',
      message: res.statusText || 'request failed',
      status: res.status,
      url,
    }
    emitError(fallbackDetail)
    throw new ApiClientError(res.status, { code: fallbackDetail.code, message: fallbackDetail.message })
  }

  // 成功响应：兼容两种 shape
  //   1. envelope：{success: true, data: T}（PostgREST / 旧 RPC 风格）
  //   2. 扁平：T 直接（多数 dogdisk-server gapi handlers writeJSON 业务对象）
  if (json && typeof json === 'object' && 'success' in (json as Record<string, unknown>)) {
    const env = json as ApiResponse<T>
    if (env.success === false) {
      throw new ApiClientError(res.status, env.error)
    }
    return env.data
  }
  return json as T
}

export const apiClient: ApiClient = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
  del: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
  request,
}
